import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Landmark, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const STORAGE_KEY = 'funder_gate_token';

export const FunderGateContext = React.createContext({ gateToken: null, clear: () => {} });

const FunderGate = ({ children }) => {
  const { getAuthHeaders } = useAuth();
  const [gateToken, setGateToken] = useState(() => sessionStorage.getItem(STORAGE_KEY));
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (gateToken) sessionStorage.setItem(STORAGE_KEY, gateToken);
    else sessionStorage.removeItem(STORAGE_KEY);
  }, [gateToken]);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/funder/gate`, { password }, { headers: getAuthHeaders() });
      setGateToken(r.data.gate_token);
      toast.success('Funder section unlocked');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid password');
    } finally {
      setSubmitting(false);
    }
  };

  const clear = () => setGateToken(null);

  if (gateToken) {
    return (
      <FunderGateContext.Provider value={{ gateToken, clear }}>
        {children}
      </FunderGateContext.Provider>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="funder-gate">
      <Card className="w-full max-w-md border-2 border-indigo-200">
        <CardContent className="p-8 space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Landmark className="w-6 h-6 text-indigo-700" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-indigo-950">Funder Section Locked</h2>
              <p className="text-xs text-slate-500">Enter the section password to continue</p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <Label>Password</Label>
            <Input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••"
              data-testid="funder-gate-password"
            />
            <Button type="submit" disabled={submitting || !password} className="w-full bg-indigo-950 hover:bg-indigo-900" data-testid="funder-gate-submit">
              <ShieldCheck className="w-4 h-4 mr-2" />
              {submitting ? 'Unlocking…' : 'Unlock'}
            </Button>
          </form>
          <p className="text-xs text-slate-400 text-center">Gate lasts for 8 hours per browser session.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export const useFunderGate = () => React.useContext(FunderGateContext);

export default FunderGate;
